import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError, fmtDate, API_BASE } from "../lib/api";
import { Paperclip, Upload, Download, Trash2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";
import { Button } from "./ui/button";

/**
 * AttachmentsPanel — reusable for any entity.
 * Props: entityType (pr|rfq|quotation|po|gr|invoice), entityId
 */
export default function AttachmentsPanel({ entityType, entityId }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get(`/attachments/${entityType}/${entityId}`);
            setItems(r.data);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (entityId) load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Dosya 10 MB'dan büyük olamaz");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/attachments/${entityType}/${entityId}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Dosya yüklendi");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const download = (att) => {
        const url = `${API_BASE}/attachments/download/${att.id}`;
        window.open(url, "_blank");
    };

    const remove = async (att) => {
        if (!window.confirm(`"${att.filename}" silinsin mi?`)) return;
        try {
            await api.delete(`/attachments/${att.id}`);
            toast.success("Silindi");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const canUpload = can(user, "attachment.upload");
    const canDelete = can(user, "attachment.delete");

    return (
        <div className="bg-card border border-border rounded-md" data-testid="attachments-panel">
            <div className="p-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Paperclip size={14} /> Ekler
                    <span className="text-xs text-muted-foreground font-normal">({items.length})</span>
                </div>
                {canUpload && (
                    <>
                        <input
                            ref={inputRef}
                            type="file"
                            className="hidden"
                            data-testid={`att-input-${entityType}`}
                            onChange={upload}
                        />
                        <Button
                            data-testid={`att-upload-${entityType}`}
                            size="sm"
                            variant="outline"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            className="h-8 gap-1.5"
                        >
                            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                            Yükle
                        </Button>
                    </>
                )}
            </div>
            <div className="divide-y divide-border">
                {loading ? (
                    <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 size={16} className="inline animate-spin mr-2" />Yükleniyor</div>
                ) : items.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        <FileText size={20} className="mx-auto mb-1.5 opacity-40" />
                        Ek yok
                    </div>
                ) : (
                    items.map((a) => (
                        <div key={a.id} className="px-3 py-2.5 flex items-center gap-3 hover:bg-muted/50 group">
                            <FileText size={16} className="text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{a.filename}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    {(a.size / 1024).toFixed(1)} KB • {a.uploaded_by_name} • {fmtDate(a.created_at)}
                                </div>
                            </div>
                            <button
                                data-testid={`att-download-${a.id}`}
                                onClick={() => download(a)}
                                className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground"
                                title="İndir"
                            >
                                <Download size={14} />
                            </button>
                            {canDelete && (
                                <button
                                    data-testid={`att-delete-${a.id}`}
                                    onClick={() => remove(a)}
                                    className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-rose-600"
                                    title="Sil"
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
